import React, {useState, useEffect} from "react"
import {useForm, Controller} from "react-hook-form"
import styled from "styled-components"
import moment from "moment"
// Components
import DropDown from "components/shared/DropDown"
import Datepicker from "components/shared/DatePicker"
// UI
import {extended, semantic} from "@uprise/colors"
import {Container, Row, Col} from "@uprise/grid"
import {TextInputHorizontal} from "@uprise/form"
import {H6} from "@uprise/headings"
import {Button} from "@uprise/button"

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	align-self: center;
`

const FormTextArea = styled.textarea`
	border: none;
	resize: none;
	width: 100%;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100px;
	height: 50px;
	margin: 0 6px;
`

const SecondaryButton = styled(Button)`
	width: 100px;
	height: 50px;
	margin: 0 6px;
	border-color: transparent;
	background-color: #f6f4fc;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
`

const Contracts = ({onComplete, onPrevious, triggerValidation}) => {
	const {register, control, handleSubmit, watch, reset, trigger, setError, errors, clearErrors} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onChange",
		reValidateMode: "onChange"
	})

	useEffect(() => {
		if (triggerValidation) {
			trigger()
		}
	}, [triggerValidation, trigger])

	const watchDates = watch(["dateSigned", "expiryDate"])

	const dateCheck = () => {
		return !!(watchDates.dateSigned && (watchDates?.dateSigned).diff(watchDates.expiryDate) > 0)
			? "Date Signed cant exceed Date of Expiry"
			: true
	}
	const dateCheckExpiry = () => {
		return !!(watchDates.expiryDate && (watchDates?.expiryDate).diff(watchDates.dateSigned) < 0)
			? "Date of Expiry cant be before Date Signed"
			: true
	}

	return (
		<Container>
			<form
				onSubmit={handleSubmit(data => {
					onComplete(data)
				})}>
				<Row>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer error={errors.contractStatus?.type === "required"}>
							<FormElementLabel isRequired={true}>Contract Status</FormElementLabel>
							<FormInputContainer>
								<Controller
									isFirst={true}
									name={"contractStatus"}
									control={control}
									defaultValue={""}
									rules={{required: "Contract Status is Required"}}
									render={props => (
										<DropDown
											variant='primary'
											testId='contractStatus'
											items={[
												{
													label: "Current",
													value: "current"
												},
												{
													label: "Expired",
													value: "expired"
												}
											]}
											isFirst={true}
											label='Contract Status'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={""}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["contractStatus"] && errors["contractStatus"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["contractStatus"]}>
								{errors["contractStatus"] && errors["contractStatus"].message}
							</MessageStyles>
						) : null}
						<FormElementContainer error={errors.dateSigned?.type === "required"}>
							<FormElementLabel isRequired={true}>Date Signed</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"dateSigned"}
									control={control}
									rules={{required: "Date Signed is Required", validate: dateCheck}}
									defaultValue={moment()}
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => props.onChange(e.target.value)}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["dateSigned"] && errors["dateSigned"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["dateSigned"]}>
								{errors["dateSigned"] && errors["dateSigned"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer error={errors.expiryDate?.type === "required"}>
							<FormElementLabel isRequired={true}>Date of Expiry</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"expiryDate"}
									control={control}
									rules={{required: "Date of Expiry is Required", validate: dateCheckExpiry}}
									defaultValue={moment().add(1, "years")}
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => props.onChange(e.target.value)}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["expiryDate"] && errors["expiryDate"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["expiryDate"]}>
								{errors["expiryDate"] && errors["expiryDate"].message}
							</MessageStyles>
						) : null}
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Link to Pipedrive</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"pipedrive"}
									control={control}
									defaultValue={""}
									as={
										<TextInputHorizontal
											id='pipedrive'
											type='text'
											placeholder='Link to Pipedrive'
										/>
									}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel>Link to Gdrive</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"gdrive"}
									defaultValue={""}
									control={control}
									as={<TextInputHorizontal id='gDrive' type='text' placeholder='Link to Gdrive' />}
								/>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
				</Row>
				<Row className='flex-row-reverse'>
					<ButtonContainer>
						<PrimaryButton type='submit' variant={"primary"} title={"Next"} data-testid='nextButton' />
					</ButtonContainer>
					<ButtonContainer>
						<SecondaryButton
							type='button'
							variant={"secondary"}
							title={"Previous"}
							onClick={onPrevious}
							data-testid='previousButton'
						/>
					</ButtonContainer>
				</Row>
			</form>
		</Container>
	)
}

export default Contracts
